import React, { useState, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import connectionsData from '@/data/connections.json';

interface Connection {
  n: string;
  c: string;
  p: string;
  d: string;
}

const connections = connectionsData as Connection[];

const fuzzyMatch = (query: string, target: string): number => {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 90;
  if (t.includes(q)) return 80;
  const queryWords = q.split(/\s+/);
  const targetWords = t.split(/\s+/);
  let score = 0;
  for (const qWord of queryWords) {
    for (const tWord of targetWords) {
      if (tWord === qWord) score += 30;
      else if (tWord.startsWith(qWord)) score += 20;
      else if (tWord.includes(qWord)) score += 10;
    }
  }
  return score;
};

// Elegant confetti with gold/warm tones
const Confetti = () => {
  const colors = ['#fbbf24', '#f59e0b', '#d97706', '#ffffff', '#fef3c7'];
  const confettiPieces = Array.from({ length: 60 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    color: colors[Math.floor(Math.random() * colors.length)],
    delay: Math.random() * 0.8,
    duration: 2.5 + Math.random() * 2,
    size: 3 + Math.random() * 6,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {confettiPieces.map((piece) => (
        <motion.div
          key={piece.id}
          initial={{
            y: -20,
            x: `${piece.x}vw`,
            opacity: 1,
            rotate: piece.rotation,
            scale: 0
          }}
          animate={{
            y: '100vh',
            opacity: 0,
            rotate: piece.rotation + 360,
            scale: 1
          }}
          transition={{
            duration: piece.duration,
            delay: piece.delay,
            ease: 'linear'
          }}
          style={{
            position: 'absolute',
            width: piece.size,
            height: piece.size * 1.5,
            backgroundColor: piece.color,
            borderRadius: '1px',
          }}
        />
      ))}
    </div>
  );
};

const Connected = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [phase, setPhase] = useState<'input' | 'revealing' | 'result'>('input');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [revealStep, setRevealStep] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchResults = useMemo(() => {
    if (searchQuery.length < 2) return [];
    return connections
      .map(conn => ({ ...conn, score: fuzzyMatch(searchQuery, conn.n) }))
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
  }, [searchQuery]);

  const bestMatch = searchResults.length > 0 ? searchResults[0] : null;
  const isConnected = bestMatch && bestMatch.score >= 50;
  const displayName = isConnected ? bestMatch.n : searchQuery;

  const connectionIndex = useMemo(() => {
    if (!isConnected || !bestMatch) return null;
    const sorted = [...connections].sort((a, b) => {
      const dateA = new Date(a.d.split(' ').reverse().join('-'));
      const dateB = new Date(b.d.split(' ').reverse().join('-'));
      return dateA.getTime() - dateB.getTime();
    });
    return sorted.findIndex(c => c.n === bestMatch.n) + 1;
  }, [isConnected, bestMatch]);

  const runReveal = useCallback(() => {
    if (searchQuery.length < 2) return;
    setShowSuggestions(false);
    setPhase('revealing');
    setRevealStep(0);

    setTimeout(() => setRevealStep(1), 500);
    setTimeout(() => setRevealStep(2), 1000);
    setTimeout(() => {
      setRevealStep(3);
      setPhase('result');
    }, 1600);
  }, [searchQuery]);

  const reset = () => {
    setSearchQuery('');
    setPhase('input');
    setRevealStep(0);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div className="min-h-screen overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      {phase === 'result' && isConnected && <Confetti />}

      <AnimatePresence mode="wait">
        {/* INPUT PHASE */}
        {phase === 'input' && (
          <motion.div
            key="input"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen bg-[#FAFAF9] flex flex-col"
          >
            {/* Minimal nav */}
            <nav className="p-8">
              <button
                onClick={() => navigate('/')}
                className="text-neutral-400 hover:text-neutral-600 text-sm font-medium tracking-wide transition-colors"
              >
                ← Back
              </button>
            </nav>

            {/* Centered content */}
            <div className="flex-1 flex items-center justify-center px-6 pb-32">
              <div className="w-full max-w-lg">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="text-center mb-16"
                >
                  <h1
                    className="text-5xl sm:text-6xl md:text-7xl text-neutral-900 mb-6 leading-[1.1]"
                    style={{ fontFamily: 'Lora, serif', fontWeight: 400 }}
                  >
                    Are we<br />connected?
                  </h1>
                  <p className="text-neutral-500 text-lg font-light">
                    Search my professional network
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="relative"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && runReveal()}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder="Enter your name"
                    className="w-full text-2xl sm:text-3xl text-center text-neutral-900 placeholder:text-neutral-300 bg-transparent border-b-2 border-neutral-200 focus:border-neutral-900 pb-4 outline-none transition-colors font-light"
                    autoFocus
                  />

                  {/* Suggestions */}
                  <AnimatePresence>
                    {showSuggestions && searchQuery.length >= 2 && searchResults.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 right-0 mt-4 bg-white rounded-2xl shadow-[0_8px_40px_-12px_rgba(0,0,0,0.15)] border border-neutral-100 overflow-hidden z-20"
                      >
                        {searchResults.map((result, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setSearchQuery(result.n);
                              setShowSuggestions(false);
                              setTimeout(runReveal, 150);
                            }}
                            className="w-full text-left px-6 py-4 hover:bg-neutral-50 transition-colors border-b border-neutral-50 last:border-0"
                          >
                            <span className="text-neutral-900 font-medium">{result.n}</span>
                            {result.c && (
                              <span className="text-neutral-400 text-sm ml-3">{result.c}</span>
                            )}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-12 text-center"
                >
                  <button
                    onClick={runReveal}
                    disabled={searchQuery.length < 2}
                    className="px-12 py-4 bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-200 disabled:cursor-not-allowed text-white text-lg font-medium rounded-full transition-all"
                  >
                    Find out
                  </button>
                </motion.div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="text-center text-neutral-400 text-sm mt-16 tracking-wide"
                >
                  {connections.length.toLocaleString()} connections · Searched locally
                </motion.p>
              </div>
            </div>
          </motion.div>
        )}

        {/* REVEALING PHASE */}
        {phase === 'revealing' && (
          <motion.div
            key="revealing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center bg-neutral-950"
          >
            <div className="text-center px-6">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: revealStep >= 0 ? 0.5 : 0 }}
                className="text-neutral-500 text-sm tracking-[0.2em] uppercase mb-10"
              >
                Searching
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: revealStep >= 1 ? 1 : 0,
                  y: revealStep >= 1 ? 0 : 20
                }}
                transition={{ duration: 0.5 }}
                className="text-4xl sm:text-6xl md:text-7xl text-white mb-8"
                style={{ fontFamily: 'Lora, serif', fontWeight: 400 }}
              >
                {displayName}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: revealStep >= 2 ? 1 : 0 }}
                className="flex justify-center gap-1.5"
              >
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.2
                    }}
                    className="w-1.5 h-1.5 bg-amber-500 rounded-full"
                  />
                ))}
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* RESULT PHASE */}
        {phase === 'result' && (
          <motion.div
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`min-h-screen flex flex-col ${isConnected ? 'bg-neutral-950' : 'bg-[#FAFAF9]'}`}
          >
            {isConnected && bestMatch ? (
              /* CONNECTED */
              <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center relative overflow-hidden">
                {/* Subtle ambient glow */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(ellipse at 50% 30%, rgba(251, 191, 36, 0.08) 0%, transparent 60%)'
                  }}
                />

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="relative z-10 text-amber-500/90 text-xs font-medium tracking-[0.3em] uppercase mb-10"
                >
                  Connection #{connectionIndex} of {connections.length.toLocaleString()}
                </motion.p>

                <motion.h1
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring', duration: 0.8 }}
                  className="relative z-10 text-5xl sm:text-7xl md:text-8xl text-white mb-6"
                  style={{ fontFamily: 'Lora, serif', fontWeight: 400, lineHeight: 1.1 }}
                >
                  {bestMatch.n}
                </motion.h1>

                <motion.div
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="relative z-10 w-16 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent mb-10"
                />

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="relative z-10 space-y-2 mb-12"
                >
                  {(bestMatch.p || bestMatch.c) && (
                    <p className="text-neutral-400 text-lg font-light">
                      {bestMatch.p && <span className="text-neutral-300">{bestMatch.p}</span>}
                      {bestMatch.p && bestMatch.c && <span className="mx-3 text-neutral-600">·</span>}
                      {bestMatch.c && <span>{bestMatch.c}</span>}
                    </p>
                  )}
                  <p className="text-neutral-500 font-light">
                    Connected <span className="text-amber-500/90">{bestMatch.d}</span>
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="relative z-10 flex flex-col sm:flex-row gap-4"
                >
                  <button
                    onClick={() => navigate('/projects')}
                    className="px-8 py-3.5 bg-white text-neutral-900 font-medium rounded-full hover:bg-neutral-100 transition-colors"
                  >
                    See what I'm building →
                  </button>
                  <button
                    onClick={reset}
                    className="px-8 py-3.5 text-neutral-500 hover:text-white font-medium transition-colors"
                  >
                    Check another
                  </button>
                </motion.div>
              </div>
            ) : (
              /* NOT CONNECTED */
              <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-neutral-400 text-xs font-medium tracking-[0.3em] uppercase mb-10"
                >
                  Searched {connections.length.toLocaleString()} connections
                </motion.p>

                <motion.h1
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring', duration: 0.8 }}
                  className="text-5xl sm:text-7xl md:text-8xl text-neutral-900 mb-8"
                  style={{ fontFamily: 'Lora, serif', fontWeight: 400, lineHeight: 1.1 }}
                >
                  {searchQuery}
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="text-2xl text-neutral-500 font-light mb-16"
                >
                  Not connected <span className="text-neutral-300 italic">yet</span>
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <a
                    href="https://www.linkedin.com/in/josharsh"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-[#0A66C2] hover:bg-[#004182] text-white font-medium rounded-full transition-colors text-lg"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    Let's connect
                  </a>
                </motion.div>

                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  onClick={reset}
                  className="mt-8 text-neutral-400 hover:text-neutral-600 font-medium transition-colors"
                >
                  ← Try a different name
                </motion.button>
              </div>
            )}

            {/* Footer */}
            <motion.footer
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className={`py-8 text-center ${isConnected ? 'text-neutral-700' : 'text-neutral-400'}`}
            >
              <p className="text-sm tracking-wide">
                josharsh.me/connected
              </p>
            </motion.footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Connected;
